import { useApolloClient, useReactiveVar } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { SET_VACANCY_CHANNEL } from 'graphql/vacancies/mutations';
import { GET_VACANCY_CHANNELS } from 'graphql/vacancies/queries';
import { AdChannel } from 'types/adTypes';
import { VacancySpendStatus } from 'types/vacancyTypes';
import { ToastTypes } from 'types/notificationTypes';
import { useMutation } from './sympl-mutation';
import useNavigationContext from './context/nav-context';
import { useToastNotifications } from './notificationHooks';
import {
  acknowledgeVacancyChannelIntent,
  beginVacancyChannelIntent,
  beginVacancyChannelWrite,
  completeVacancyChannelIntent,
  confirmVacancyChannels,
  getVacancyChannelState,
  getVacancyChannelIntents,
  setVacancyChannelOperation,
  VacancyChannelIntent,
} from 'utils/vacancyChannels';

export const useVacancyChannelSelection = (requireActiveSpending = true) => {
  const { activeVacancy, activeCustomer, currentVacancy, vacancyLoading } =
    useNavigationContext();
  const client = useApolloClient();
  const { addToast } = useToastNotifications();
  const { operations } = getVacancyChannelState(client.cache);
  useReactiveVar(operations);
  const entries = getVacancyChannelIntents(client.cache, activeVacancy ?? 0);
  const pending = entries.some(({ operation }) => operation === 'pending');
  const unconfirmed = entries.some(({ operation }) => operation === 'unknown');
  const recoveries = entries
    .filter(
      ({ operation }) => operation === 'unknown' || operation === 'failed'
    )
    .map(({ intent, operation }) => ({
      channel: intent.channel,
      recorded: operation === 'failed',
      disabled: pending || (unconfirmed && operation !== 'unknown'),
    }));
  const channels =
    currentVacancy?.key === activeVacancy ? currentVacancy?.channels ?? [] : [];
  const activeSpending =
    currentVacancy?.key === activeVacancy &&
    currentVacancy?.spend_status === VacancySpendStatus.ACTIVE;
  const disabled =
    !activeCustomer ||
    !activeVacancy ||
    currentVacancy?.key !== activeVacancy ||
    vacancyLoading ||
    (requireActiveSpending && !activeSpending) ||
    pending ||
    unconfirmed;
  const [setChannel] = useMutation<
    { setChannel: { channels: AdChannel[] } },
    {
      vacancyId: number;
      input: { channel: string; enabled: boolean; request_id: string };
    }
  >(SET_VACANCY_CHANNEL);

  const submitIntent = async (
    vacancyId: number,
    intent: VacancyChannelIntent
  ) => {
    setVacancyChannelOperation(
      client.cache,
      vacancyId,
      intent.channel,
      'pending'
    );
    const context = {
      headers: { 'Active-Customer': String(intent.customerId) },
    };
    const refreshChannels = async () => {
      // A PUT response can predate another writer's selection. Reads still in flight
      // cannot establish that order, so confirm with a new read after the acknowledgement.
      const result = await client.query<{
        vacancy: { channels: AdChannel[] };
      }>({
        query: GET_VACANCY_CHANNELS,
        variables: { id: vacancyId },
        fetchPolicy: 'network-only',
        context: { ...context, queryDeduplication: false },
      });
      if (!Array.isArray(result.data?.vacancy?.channels))
        throw new Error('Channel selection is missing.');
    };
    for (let attempt = 0; attempt < 2; attempt++) {
      const startedOrder = beginVacancyChannelWrite(client.cache);
      try {
        const { data } = await setChannel({
          variables: {
            vacancyId,
            input: {
              channel: intent.channel,
              enabled: intent.enabled,
              request_id: intent.requestId,
            },
          },
          context,
          update: (cache, { data }) => {
            const confirmed = data?.setChannel?.channels;
            if (Array.isArray(confirmed))
              confirmVacancyChannels(cache, vacancyId, confirmed, startedOrder);
          },
        });
        if (!Array.isArray(data?.setChannel?.channels))
          throw new Error('Channel selection is missing.');
        await refreshChannels();
        completeVacancyChannelIntent(client.cache, vacancyId, intent.requestId);
        addToast({
          description: 'The saved channel selection has been confirmed.',
          type: ToastTypes.SUCCESS,
        });
        return true;
      } catch (error) {
        const network = (
          error as {
            networkError?: {
              statusCode?: number;
              result?: {
                data?: {
                  command_recorded?: boolean;
                  request_id?: string;
                  channels?: AdChannel[];
                };
              };
            };
          }
        )?.networkError;
        const status = network?.statusCode;
        if (attempt === 0 && (status === undefined || status >= 500)) continue;
        const recorded = network?.result?.data;
        if (
          recorded?.command_recorded === true &&
          recorded.request_id === intent.requestId &&
          Array.isArray(recorded.channels)
        ) {
          try {
            confirmVacancyChannels(
              client.cache,
              vacancyId,
              recorded.channels,
              startedOrder
            );
            await refreshChannels();
            if (
              acknowledgeVacancyChannelIntent(
                client.cache,
                vacancyId,
                intent.requestId
              )
            ) {
              addToast({
                description:
                  'The channel change could not be completed. You can retry it or choose different channels.',
                type: ToastTypes.ERROR,
              });
              return false;
            }
          } catch (_) {
            /* Keep the unresolved request protected if its recovery state cannot be saved. */
          }
        }
        break;
      }
    }
    setVacancyChannelOperation(
      client.cache,
      vacancyId,
      intent.channel,
      'unknown'
    );
    addToast({
      description:
        'The channel change could not be confirmed. Retry the same change to confirm its result.',
      type: ToastTypes.ERROR,
    });
    return false;
  };

  const changeChannel = async (
    channel: AdChannel,
    enabled = !channels.includes(channel)
  ) => {
    if (
      disabled ||
      !activeVacancy ||
      !activeCustomer ||
      getVacancyChannelIntents(client.cache, activeVacancy).some(
        ({ operation }) => operation === 'pending' || operation === 'unknown'
      )
    )
      return false;
    const intent: VacancyChannelIntent = {
      customerId: activeCustomer,
      channel,
      enabled,
      requestId: uuidv4(),
    };
    try {
      if (!beginVacancyChannelIntent(client.cache, activeVacancy, intent))
        return false;
    } catch (_) {
      addToast({
        description:
          'Your browser could not save this channel change. Please enable browser storage and try again.',
        type: ToastTypes.ERROR,
      });
      return false;
    }
    return submitIntent(activeVacancy, intent);
  };
  const retryChange = async (channel?: AdChannel) => {
    if (!activeVacancy) return;
    const current = getVacancyChannelIntents(client.cache, activeVacancy);
    if (current.some(({ operation }) => operation === 'pending')) return;
    const unconfirmed = current.some(
      ({ operation }) => operation === 'unknown'
    );
    const entry = current.find(
      ({ intent, operation }) =>
        (!channel || intent.channel === channel) &&
        (operation === 'unknown' || (!unconfirmed && operation === 'failed'))
    );
    if (entry) await submitIntent(activeVacancy, entry.intent);
  };
  return {
    channels,
    activeSpending,
    disabled,
    pending,
    unknown: recoveries.length > 0,
    failureKnown: recoveries.length > 0 && !unconfirmed,
    recoveries,
    vacancyLoading,
    changeChannel,
    retryChange,
  };
};
